import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Badge } from './ui/badge';
import { X, User, Briefcase, Contact } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MENTION_TYPES = {
  '@': 'member',
  '#J': 'job',
  '#C': 'contact'
};

const TYPE_ICONS = {
  member: User,
  job: Briefcase,
  contact: Contact
};

const TYPE_LABELS = {
  member: 'Member',
  job: 'Job',
  contact: 'Contact'
};

export const MentionTextarea = ({ value, onChange, placeholder, rows, references = [], onReferencesChange }) => {
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteResults, setAutocompleteResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionType, setMentionType] = useState(null);
  const [mentionQuery, setMentionQuery] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (mentionType !== null) {
      fetchAutocomplete(mentionQuery, mentionType);
    } else {
      setAutocompleteResults([]);
    }
  }, [mentionQuery, mentionType]);

  const fetchAutocomplete = async (query, type) => {
    try {
      const token = localStorage.getItem('access_token');
      const params = { query, type };
      const config = {
        params,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      };
      const response = await axios.get(`${API}/notes/references`, config);
      setAutocompleteResults(response.data || []);
    } catch (error) {
      console.error('Error fetching autocomplete:', error);
      setAutocompleteResults([]);
    }
  };

  const handleTextChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    onChange(e);
    setCursorPosition(cursorPos);

    // Check for mention trigger (@, #J/#j, or #C/#c) - case insensitive
    const textBeforeCursor = newValue.substring(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@(\w*)|#([JjCc])(\w*)$/);

    if (mentionMatch) {
      if (mentionMatch[0].startsWith('@')) {
        // @ mention
        const query = mentionMatch[1];
        setMentionType('member');
        setMentionQuery(query);
        setShowAutocomplete(true);
        setSelectedIndex(0);
      } else if (mentionMatch[0].match(/#[Jj]/)) {
        // #J or #j for jobs
        const query = mentionMatch[3] || '';
        setMentionType('job');
        setMentionQuery(query);
        setShowAutocomplete(true);
        setSelectedIndex(0);
      } else if (mentionMatch[0].match(/#[Cc]/)) {
        // #C or #c for contacts
        const query = mentionMatch[3] || '';
        setMentionType('contact');
        setMentionQuery(query);
        setShowAutocomplete(true);
        setSelectedIndex(0);
      }
    } else {
      setShowAutocomplete(false);
      setMentionType(null);
      setMentionQuery('');
    }
  };

  const insertMention = (item) => {
    const textBeforeCursor = value.substring(0, cursorPosition);
    const textAfterCursor = value.substring(cursorPosition);
    
    // Find and replace the mention trigger and query (case insensitive)
    const mentionRegex = /@\w*|#[JjCc]\w*$/;
    const newTextBefore = textBeforeCursor.replace(mentionRegex, '');
    
    // Create mention text with appropriate trigger
    let trigger;
    if (mentionType === 'member') {
      trigger = '@';
    } else if (mentionType === 'job') {
      trigger = '#J';
    } else if (mentionType === 'contact') {
      trigger = '#C';
    }
    const mentionText = `${trigger}${item.name} `;
    
    const newValue = newTextBefore + mentionText + textAfterCursor;
    
    // Update content
    onChange({ target: { value: newValue } });
    
    // Add to references if not already there
    if (!references.find(ref => ref.id === item.id && ref.type === item.type)) {
      onReferencesChange([...references, {
        type: item.type,
        id: item.id,
        name: item.name,
        subtitle: item.subtitle
      }]);
    }
    
    setShowAutocomplete(false);
    setMentionType(null);
    setMentionQuery('');
    
    // Set cursor position after mention
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = newTextBefore.length + mentionText.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (!showAutocomplete || autocompleteResults.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % autocompleteResults.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + autocompleteResults.length) % autocompleteResults.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertMention(autocompleteResults[selectedIndex]);
    } else if (e.key === 'Escape') {
      setShowAutocomplete(false);
    }
  };

  const removeReference = (refToRemove) => {
    onReferencesChange(references.filter(ref => !(ref.id === refToRemove.id && ref.type === refToRemove.type)));
  };

  return (
    <div className="space-y-2">
      {/* References Display */}
      {references && references.length > 0 && (
        <div className="space-y-1">
          <label className="text-sm font-medium">Referenced Items:</label>
          <div className="flex flex-wrap gap-2 p-2 border rounded-lg bg-muted/30 min-h-[2.5rem]">
            {references.map((ref, idx) => {
              const Icon = TYPE_ICONS[ref.type];
              return (
                <Badge 
                  key={`${ref.type}-${ref.id}-${idx}`}
                  variant="outline" 
                  className="flex items-center gap-1 px-2 py-1"
                >
                  <Icon className="h-3 w-3" />
                  <span className="font-medium">{TYPE_LABELS[ref.type]}:</span>
                  <span>{ref.name}</span>
                  <button
                    onClick={() => removeReference(ref)}
                    className="ml-1 hover:text-red-600"
                    title="Remove reference"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">
            Type <kbd className="px-1 bg-muted rounded">@</kbd> for members, 
            <kbd className="px-1 bg-muted rounded ml-1">#J</kbd> for jobs, 
            <kbd className="px-1 bg-muted rounded ml-1">#C</kbd> for contacts
          </p>
        </div>
      )}

      {/* Textarea with Autocomplete */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || "Type @ for members, #J for jobs, #C for contacts..."}
          rows={rows || 10}
          className="flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
        />

        {/* Autocomplete Dropdown */}
        {showAutocomplete && autocompleteResults.length > 0 && (
          <div className="absolute bottom-full mb-2 left-0 right-0 bg-popover border rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
            {autocompleteResults.map((item, index) => {
              const Icon = TYPE_ICONS[item.type];
              return (
                <button
                  key={`${item.type}-${item.id}`}
                  onClick={() => insertMention(item)}
                  className={`w-full text-left px-3 py-2 hover:bg-accent transition-colors flex items-center gap-2 ${
                    index === selectedIndex ? 'bg-accent' : ''
                  }`}
                >
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{item.name}</div>
                    {item.subtitle && (
                      <div className="text-xs text-muted-foreground truncate">{item.subtitle}</div>
                    )}
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {TYPE_LABELS[item.type]}
                  </Badge>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
